import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import React from 'react';
import { ExposureConfigurationResponse } from '../../../api/ConfigApi';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { Peril } from '../../../types/policy';
import LimitsForm from '../Perils/LimitsForm';
import { filterForPolicySnapshot } from '../util_sharedEndorsementAndRenewal';
import ReviewChangesField from './ReviewChangesField';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	peril: Peril;
	characteristicTimestamp: string;
	referenceTimestamp: string;
	exposureConfiguration: ExposureConfigurationResponse;
}

const ReviewChangesPeril = (props: Props) => {
	const {
		peril,
		characteristicTimestamp,
		referenceTimestamp,
		exposureConfiguration,
		policyData,
		authData,
	} = props;
	const { policy } = policyData;
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const perilConfig = exposureConfiguration.perils.find(
		(config) => config.name === peril.name,
	);
	const perilCharacteristic = peril.characteristics
		.filter((c) =>
			filterForPolicySnapshot(
				c.createdTimestamp,
				c.replacedTimestamp,
				referenceTimestamp,
			),
		)
		.find(
			(char) =>
				parseInt(char.coverageStartTimestamp, 10) <=
					parseInt(characteristicTimestamp, 10) &&
				parseInt(char.coverageEndTimestamp, 10) >=
					parseInt(characteristicTimestamp, 10),
		);

	if (!perilCharacteristic) {
		return null;
	}

	const renderFields = () => {
		const fieldConfigs = perilConfig.fields;
		return fieldConfigs.map((field, ind) => {
			const value = perilCharacteristic.fieldValues[field.name] || '--';
			return (
				<Grid item key={`${field}-${ind}`}>
					<ReviewChangesField
						field={field}
						value={value}
						fieldGroupsByLocator={null}
						allValues={perilCharacteristic.fieldValues}
						currentFieldValues={perilCharacteristic.fieldValues}
						addFieldGroups={perilCharacteristic.addFieldGroups}
					/>
				</Grid>
			);
		});
	};

	const renderLimits = () => {
		return (
			<LimitsForm
				perilObj={perilCharacteristic}
				id={peril.locator}
				perilConfig={perilCharacteristic}
				currency={policy?.currency}
				isInEditMode={false}
				userHasWriteAccess={false}
				updateLimit={null}
			/>
		);
	};

	return (
		<Accordion key={`peril-${peril.locator}`}>
			<AccordionSummary
				style={{ borderBottom: '1px solid rgba(0, 0, 0, .125)' }}
				expandIcon={<ExpandMoreIcon />}
			>{`${peril.name}`}</AccordionSummary>
			<AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
				<Grid container spacing={1}>
					{renderFields()}
				</Grid>
				{!hidePerilLimitsEnabled && renderLimits()}
			</AccordionDetails>
		</Accordion>
	);
};

export default withPolicyContext<Props>(ReviewChangesPeril);
